import useManuscriptReportChoice from "../../hooks/useManuscriptReportChoice";
import ManuscriptReportChoice from "../ManuscriptReportChoice";
import { getBrowserTimeZone } from "../../utils/getBrowserTimeZone";
import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { Button } from "../Button";
import SVG from "react-inlinesvg";
import Auth from "../../Auth";
import InlineSVG from "react-inlinesvg";
import Dropzone from "react-dropzone";
import ProgressContainer from "../ProgressContainer";
import { MAX_TRIAL_NOVEL_IMPORTS, MAX_IMPORT_WIP_INPUT_WORDS } from "../../../../src/shared/constants";
import { createId } from "@paralleldrive/cuid2";
import { useToast } from "../ToastWrapper";
import { sendAppEvent } from "../../Utils";

const ImportModalContainer = styled.div`
  position: fixed;
  z-index: 10;
  top: 33%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 570px;
  min-height: 275px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: var(--menu-color);
  border-radius: 30px;
  box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.15);

  .import-screen-title {
    margin-top: 50px;
    font-size: 30px !important;
    font-family: "GT Super Text";
    font-weight: 400;
    color: var(--grey-0);
  }

  .import-screen-subtitle {
    font-size: 18px;
    font-family: "GT Super Text";
    font-weight: 400;
    color: var(--grey-1);
  }

  .import-screen-buttons {
    display: flex;
    gap: 16px;
    margin-top: 50px;

    .import-progress-bar {
      border-radius: 10px;
    }

    svg.import {
      margin-right: 5px;
      padding-left: 4px;
      path {
        fill: none;
        stroke: var(--cta-text-color);
      }
    }

    p.import-button-text {
      padding: 2px 6px 4px 0px;
    }

    p.skip-button-text {
      padding: 2px 4px 4px 4px;
    }
  }
`;

const ImportScreen = ({ setShow, user, userData }) => {
  const [importing, setImporting] = useState(false);
  const reportChoice = useManuscriptReportChoice();
  const { setToast } = useToast();
  useEffect(() => {
    sendAppEvent("signup_import_novel_viewed");
  }, []);


  const maxImportWords = MAX_IMPORT_WIP_INPUT_WORDS;

  const getMessage = (error) => {
    const message = (() => {
      switch (error) {
        case "Invalid file type":
          return "Could not upload your file. Please upload a .txt, .doc, .docx, .rtf, or .odt file.";
        case "Max words exceeded":
          return `Could not import your project because it exceeds the maximum word count. Please upload a file with ${maxImportWords.toLocaleString()} words or fewer.`;
        case "Max imports for trial user exceeded.":
          return `You can only import ${MAX_TRIAL_NOVEL_IMPORTS} novels while on your trial. Subscribe to import more.`;
        default:
          return "Could not import your project. Please try again.";
      }
    })();

    return (
      <>
        <InlineSVG
          src="/assets/close.svg"
          className="failed"
        />
        {message}
      </>
    );
  };

  const createImportedProject = async (file) => {
    try {
      const { folder: homeFolder } = await Auth.fetchWithIdToken("/api/folders/home", { user });
      if (!homeFolder?.id) {
        throw new Error("Could not find home folder");
      }

      const projectId = createId();
      const title = file.name.replace(/\.[^/.]+$/, "");
      const { project } = await Auth.fetchWithIdToken("/api/projects/import", {
        title,
        source: "import-wip",
        folderParentId: homeFolder.id,
        folderProjectId: projectId,
      });

      if (!project?.id) {
        throw new Error("Invalid project response");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", project.id);
      const result = await Auth.postFilesWithIdToken("/api/import", { user, projectId: project.id }, formData);

      if (!result?.document?.id) {
        throw new Error("Invalid document response");
      }

      return { projectId: project.id, documentId: result.document.id };
    } catch (error) {
      setImporting(false);
      setToast(getMessage(error.message));
      throw error;
    }
  };

  const handleImport = async (documentId, projectId, originalFilename) => {
    if (!documentId || !projectId) {
      setImporting(false);
      setToast(getMessage("Could not import your project"));
      throw new Error("Missing required IDs for import");
    }

    try {
      await Auth.fetchWithIdToken("/api/ai/storyEngine/importNovel", { projectId, documentId, originalFilename, timeZone: getBrowserTimeZone(), enableImportReport: reportChoice.enableImportReport });
      await userData.update({ needsImport: false, hasImportedOrSkipped: true });
      setShow(false);
    } catch (err) {
      const { error } = await err.response.json();
      setToast(getMessage(error));
    }
  };

  const handleAccept = async (file) => {
    // Note: We're not checking for trial users who have exceeded the max amount of imports, because this component only shows up in the onboarding, it will always be the first import
    const { projectId, documentId } = await createImportedProject(file);
    await handleImport(documentId, projectId, file.name);
    setImporting(false);
  };

  const handleDecline = async () => {
    sendAppEvent("signup_import_novel_skip_clicked");
    await userData.update({ needsImport: false, hasImportedOrSkipped: true });
    setShow(false);
  };
  return (
    <ImportModalContainer>
      <h2 className="import-screen-title">Import your work-in-progress</h2>
      <p className="import-screen-subtitle">Sudowrite will fill out a Story Bible for you</p>
      <ManuscriptReportChoice choice={reportChoice} disabled={importing} />
      <div className="import-screen-buttons">
        <Button
          tertiaryGrey={true}
          className="use-component-styles"
          onClick={handleDecline}
          aria-label="Skip import"
        >
          <p className="skip-button-text">Skip</p>
        </Button>
        <Dropzone
          accept={{
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx", ".doc"],
            "text/plain": [".txt"],
            "application/rtf": [".rtf"],
            "application/vnd.oasis.opendocument.text": [".odt"],
          }}
          onDrop={async (acceptedFiles, rejectedFiles) => {
            sendAppEvent("signup_import_novel_file_dropped");
            if (rejectedFiles.length > 0) {
              sendAppEvent("signup_import_novel_file_rejected");
              setToast(getMessage("Invalid file type"));
              return;
            }
            setImporting(true);
            try {
              await handleAccept(acceptedFiles[0]);
            } catch (error) {
              setImporting(false);
            }
          }}
          disabled={importing || !reportChoice.ready}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps({ onClick: () => sendAppEvent("signup_import_novel_click_to_browse") })}
            >
              <ProgressContainer
                isLoading={importing}
                className="import-progress-bar"
              >
                <input {...getInputProps()} />
                <Button
                  primary={true}
                  className="use-component-styles"
                  onClick={(e) => e.preventDefault()} // no-op, we're using the Dropzone
                  aria-label="Import your story"
                >
                  <SVG
                    src="/assets/import.svg"
                    height={20}
                    className="import"
                  />
                  <p className="import-button-text">Import</p>
                </Button>
              </ProgressContainer>
            </div>
          )}
        </Dropzone>
      </div>
    </ImportModalContainer>
  );
};

export default ImportScreen;
